import {
  collection,
  limit,
  orderBy,
  query,
  Timestamp,
  where,
  type DocumentSnapshot,
  type Query,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from './firebase';
import { Activity } from '../types';
import {
  ACTIVITIES_ENTITY_LIMIT,
  ACTIVITIES_RECENT_LIMIT,
  ACTIVITIES_RECENT_LOOKBACK_DAYS,
  activitiesRecentLookbackDate,
} from './activitiesQueryBounds';

export {
  ACTIVITIES_ENTITY_LIMIT,
  ACTIVITIES_RECENT_LIMIT,
  ACTIVITIES_RECENT_LOOKBACK_DAYS,
  activitiesRecentLookbackDate,
} from './activitiesQueryBounds';

function toDate(value: unknown): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value;
  if (typeof value === 'object' && value !== null && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    try {
      const d = (value as { toDate: () => Date }).toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : undefined;
    } catch {
      return undefined;
    }
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
}

function requireDate(value: unknown): Date {
  return toDate(value) ?? new Date(0);
}

export function parseActivityDoc(
  docSnap: QueryDocumentSnapshot | DocumentSnapshot,
): Activity | null {
  if (!docSnap.exists()) return null;
  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
    createdAt: requireDate(data.createdAt),
    updatedAt: requireDate(data.updatedAt),
    scheduledAt: toDate(data.scheduledAt),
    completedAt: toDate(data.completedAt),
    dueDate: toDate(data.dueDate),
  } as Activity;
}

export function parseActivityDocs(docs: QueryDocumentSnapshot[]): Activity[] {
  return docs.map(docSnap => parseActivityDoc(docSnap)).filter((a): a is Activity => a != null);
}

/** Hub, calendar, kanban, reports — bounded recent window. */
export function activitiesRecentQuery(
  days = ACTIVITIES_RECENT_LOOKBACK_DAYS,
  maxDocs = ACTIVITIES_RECENT_LIMIT,
): Query {
  const lookback = activitiesRecentLookbackDate(days);
  return query(
    collection(db, 'activities'),
    where('createdAt', '>=', Timestamp.fromDate(lookback)),
    orderBy('createdAt', 'desc'),
    limit(maxDocs),
  );
}

/** Single lead/deal/project panel — scoped history. */
export function activitiesForEntityQuery(
  entityType: 'lead' | 'deal' | 'project',
  entityId: string,
  maxDocs = ACTIVITIES_ENTITY_LIMIT,
): Query {
  return query(
    collection(db, 'activities'),
    where('entityType', '==', entityType),
    where('entityId', '==', entityId),
    orderBy('createdAt', 'desc'),
    limit(maxDocs),
  );
}
